/**
 * EventFeed — chronological feed of events.
 *
 * Used in audit log, recent-activity panel, "what happened in the last hour"
 * timelines. Severity drives icon + colour; description is optional.
 */

import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { ChartSkeleton } from "../ChartSkeleton";
import { EmptyState } from "@/lib/ui/empty-state";
import type { EventItem, WidgetState } from "../types";

export interface EventFeedProps {
  events: EventItem[];
  /** Max events to render (oldest excluded). */
  limit?: number;
  state?: WidgetState;
  className?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

const SEVERITY_META = {
  info: { Icon: Info, color: "text-info bg-info/10" },
  success: { Icon: CheckCircle2, color: "text-success bg-success/10" },
  warning: { Icon: AlertTriangle, color: "text-warning bg-warning/10" },
  danger: { Icon: XCircle, color: "text-danger bg-danger/10" },
} as const;

export function EventFeed({
  events,
  limit,
  state = { kind: "ok" },
  className,
  emptyTitle = "No events yet",
  emptyDescription = "Activity will appear here as it happens.",
}: EventFeedProps) {
  if (state.kind === "loading") {
    return <ChartSkeleton shape="events" className={className} />;
  }

  if (events.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        className={className}
      />
    );
  }

  const sliced = limit ? events.slice(0, limit) : events;

  return (
    <ol className={cn("flex flex-col", className)}>
      {sliced.map((evt, idx) => {
        const meta = SEVERITY_META[evt.severity];
        const Icon = meta.Icon;
        const isLast = idx === sliced.length - 1;
        return (
          <li
            key={evt.id}
            className={cn(
              "relative flex gap-3 pb-4",
              !isLast &&
                "before:absolute before:left-[15px] before:top-9 before:bottom-0 before:w-px before:bg-border",
            )}
          >
            <div
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full",
                meta.color,
              )}
            >
              <Icon className="size-4" />
            </div>
            <div className="flex flex-1 flex-col gap-0.5 pt-0.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium text-on-surface">
                  {evt.title}
                </p>
                <time
                  dateTime={evt.t}
                  className="text-[11px] text-on-surface-muted"
                  title={evt.t}
                >
                  {formatRelative(evt.t)}
                </time>
              </div>
              {evt.source && (
                <p className="font-mono text-[11px] text-on-surface-muted">
                  {evt.source}
                </p>
              )}
              {evt.description && (
                <p className="text-sm text-on-surface-muted">{evt.description}</p>
              )}
              {evt.tags && evt.tags.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {evt.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-on-surface-muted"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
